import { useMemo, useState } from 'react';
import { GridColDef, GridRowParams } from '@mui/x-data-grid-pro';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  getDirectoryGroupQuery,
  getDomainsQuery,
} from '../../../../api/users-api';
import AddGroupAliasDialog from './add-group-alias-dialog';
import DeleteGroupAliasDialog from './delete-group-alias-dialog';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useStore } from '../../../state/zustand-store';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { useParams } from 'react-router-dom';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import CreateButton from '../../../../shared/buttons/create-button';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import CircularProgress from '@mui/material/CircularProgress';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';

export default function GroupAliases() {
  const { directoryRoles, changeLogRoles, primaryDomain } = useUserSettings();
  const { groupEmail } = useParams() as { groupEmail: string };
  const {
    isLoading: groupLoading,
    isFetching: groupFetching,
    data: group,
  } = useQuery(getDirectoryGroupQuery(groupEmail));

  const [openAddAlias, setOpenAddAlias] = useState(false);
  const [openDeleteAlias, setOpenDeleteAlias] = useState(false);
  const queryClient = useQueryClient();
  const { colorMode } = useStore();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { data: domains } = useQuery(getDomainsQuery());

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'alias',
        headerName: 'Alias',
        editable: false,
        sortable: true,
        filterable: true,
        flex: 1,
      },
      {
        field: 'isEditable',
        headerName: 'Editable',
        editable: false,
        sortable: true,
        filterable: true,
        width: 200,
        display: 'flex',
        renderCell: (params) => {
          return params.value === true ? (
            <Check />
          ) : (
            <Tooltip arrow title="Created by Google">
              <Close />
            </Tooltip>
          );
        },
      },
    ],
    []
  );

  if (!group) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  const rows =
    group.aliases?.map((alias) => ({
      id: alias,
      alias: alias,
      isEditable: true,
    })) || [];

  const rows2 =
    group.nonEditableAliases?.map((alias) => ({
      id: alias,
      alias: alias,
      isEditable: false,
    })) || [];

  const allAlliases = [...rows, ...rows2];

  function CustomToolbar() {
    return (
      <SharedToolbar
        noColumn
        noFilter
        noQuickFilter
        primaryButtons={
          <CreateButton
            onClick={() => setOpenAddAlias(true)}
            disabled={
              !isAbleToSeeUIElement(
                directoryRoles,
                DirectoryRoles.AddGroupAliases.value
              )
            }
            label="Add Alias"
          />
        }
        secondaryButtons={
          <>
            <SharedIconButton
              variant="delete"
              onClick={() => setOpenDeleteAlias(true)}
              disabled={
                selectedRows.length === 0 ||
                !isAbleToSeeUIElement(
                  directoryRoles,
                  DirectoryRoles.DeleteGroupAliases.value
                )
              }
              tooltipLabel="Delete"
            />
            <SharedIconButton
              variant="refresh"
              onClick={() => {
                void queryClient.invalidateQueries({
                  queryKey: [UserKeys.DirectoryGroup, groupEmail],
                });
              }}
              tooltipLabel="Refresh"
            />
          </>
        }
      />
    );
  }

  return (
    <>
      <SharedDataGridClient
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Group Aliases', <AlternateEmail />),
        }}
        rows={allAlliases}
        columns={columns}
        isRowSelectable={(params: GridRowParams) =>
          params.row.isEditable === true
        }
        sx={{
          '.disable': {
            bgcolor: (theme) =>
              colorMode === 'light'
                ? theme.palette.grey[200]
                : theme.palette.grey[800],
            color: (theme) =>
              colorMode === 'light'
                ? theme.palette.grey[600]
                : theme.palette.grey[400],
            '&:hover': {
              cursor: 'not-allowed',
            },
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        getRowClassName={(params) => {
          return params.row.isEditable === false ? 'disable' : '';
        }}
        loading={groupLoading || groupFetching}
        disableColumnSelector
        checkboxSelection
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
      />
      <AddGroupAliasDialog
        open={openAddAlias}
        handleClose={() => setOpenAddAlias(false)}
        group={group}
        domains={domains || []}
        changeLogRoles={changeLogRoles}
        primaryDomain={primaryDomain}
      />
      <DeleteGroupAliasDialog
        open={openDeleteAlias}
        handleClose={() => setOpenDeleteAlias(false)}
        selectedRows={selectedRows}
        groupEmail={groupEmail}
        group={group}
        changeLogRoles={changeLogRoles}
        setSelectedRows={setSelectedRows}
      />
    </>
  );
}
